const {KitModule, registerModule} = window;
const cardColors = ['pink', 'yellow', 'blue', 'purple', 'green'];

class CardLoop extends KitModule {
  onInitialize() {
    try {
      this.$element.removeAttribute('data-init-failed');
      this.listeners = [];
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.active = 0;
      this.visible = false;
      this.loopVisible = false;
      this.sectionVisible = false;
      this.rotating = !this.motion.matches;
      this.remaining = 7000;
      this.modalOpen = false;
      this.$container = this.$element.closest('.multicard--card-loop');
      this.listen(this.$container, 'library:themechange', () => {
        if (this.ready) this.render();
      });
      const style = getComputedStyle(this.$element);
      // Keep the CSS setting unitless in milliseconds, matching the animation API.
      const duration = Number(style.getPropertyValue('--loop-indicator-duration-ms'));
      this.thumbDuration = Number.isFinite(duration) && duration > 0 ? duration : 400;
      // Native animation paints the tiny ring; JS only synchronizes pause/reset.
      this.progress = this.$progress.animate([{strokeDashoffset: 100}, {strokeDashoffset: 0}], {duration: 7000, fill: 'both'});
      this.progress.pause();
      this.$rotation.disabled = this.motion.matches;
      this.$element.setAttribute('aria-roledescription', 'carousel');
      this.$track.removeAttribute('tabindex');
      // Preserve a reader's native scrolling if the bundle arrived late.
      const stride = this.$slots[1].offsetLeft - this.$slots[0].offsetLeft;
      this.active = Math.max(0, Math.min(this.$slots.length - 1, Math.round(this.$track.scrollLeft / stride) || 0));
      this.listen(this.$element, 'click', this.onClick.bind(this));
      this.listen(this.$element, 'keydown', (event) => {
        if (!this.ready || !this.$dots.includes(event.target)) return;
        const targets = {ArrowLeft: this.active - 1, ArrowRight: this.active + 1, Home: 0, End: this.$dots.length - 1};
        if (!(event.key in targets)) return;
        event.preventDefault();
        this.select(targets[event.key]);
        this.$dots[this.active].focus({preventScroll: true});
      });
      this.listen(this.$element, 'focusin', (event) => {
        if (this.ready && event.target !== this.$rotation) this.stopRotation();
      });
      this.listen(this.$stage, 'pointerenter', (event) => {
        if (!this.ready || event.pointerType !== 'mouse') return;
        this.hovering = true;
        this.schedule();
      });
      this.listen(this.$stage, 'pointerleave', () => {
        if (!this.ready) return;
        this.hovering = false;
        this.schedule();
      });
      this.listen(this.$stage, 'pointerdown', this.onPointerDown.bind(this));
      this.listen(this.$stage, 'pointermove', this.onPointerMove.bind(this));
      this.listen(this.$stage, 'pointerup', this.onPointerEnd.bind(this));
      this.listen(this.$stage, 'pointercancel', this.onPointerEnd.bind(this));
      this.listen(this.$stage, 'lostpointercapture', this.onPointerEnd.bind(this));
      this.listen(document, 'visibilitychange', () => {
        if (this.ready) this.schedule();
      });
      this.listen(document, 'focusin', (event) => {
        if (this.ready) this.dotOwnsFocus = this.$dots.includes(event.target);
      });
      this.listen(document, 'pointerdown', (event) => {
        if (this.ready && !this.$dots.includes(event.target.closest('button'))) this.dotOwnsFocus = false;
      });
      this.listen(this.motion, 'change', () => {
        if (!this.ready) return;
        this.moveThumb(false);
        if (this.motion.matches) this.stopRotation();
        else this.schedule();
      });
      this.listen(document, 'toggle', (event) => {
        if (!this.ready || !event.target.id?.startsWith(`modal-${this.$track.id.replace('-track', '-modal-')}`)) return;
        this.modalOpen = event.newState === 'open';
        if (this.modalOpen) this.stopRotation();
      }, true);
      this.measurePitch();
      this.render();
      this.$track.scrollLeft = 0;
      this.$previewsRoot.hidden = false;
      // Deferred styles can apply after setup without a window resize.
      this.dotObserver = new ResizeObserver(() => {
        if (this.ready) this.onViewportResize();
      });
      this.dotObserver.observe(this.$dotGroup);
      this.$section = this.$element.closest('.multicard--card-loop.is-parallax');
      if (this.$section) {
        // A fixed descendant can intersect after its clipped section has left.
        this.sectionObserver = new IntersectionObserver(([entry]) => {
          if (!this.ready) return;
          this.sectionVisible = entry.isIntersecting;
          this.updateVisibility();
        });
        this.sectionObserver.observe(this.$section);
      }
      // KitModule's observer survives failed setup, so every hook checks this.
      this.ready = true;
    } catch (error) {
      this.unmount();
      throw error;
    }
  }

  listen(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this.listeners.push(() => target.removeEventListener(type, handler, options));
  }

  onViewportEnter() {
    if (!this.ready) return;
    this.loopVisible = true;
    this.updateVisibility();
  }

  onViewportExit() {
    if (!this.ready) return;
    this.loopVisible = false;
    this.updateVisibility();
  }

  updateVisibility() {
    // CSS selects the section only while the xl parallax layout is fixed.
    const observeSection = this.$section && getComputedStyle(this.$element).getPropertyValue('--loop-observe-section').trim() === '1';
    this.visible = observeSection ? this.sectionVisible : this.loopVisible;
    this.schedule();
  }

  measurePitch() {
    // Cache geometry at setup, resize and stylesheet application.
    this.pitch = this.$dots[1].offsetLeft - this.$dots[0].offsetLeft;
  }

  onViewportResize() {
    if (!this.ready) return;
    this.measurePitch();
    // CSS can hide the focused dot before the resize callback runs.
    if (!this.pitch && (this.dotOwnsFocus || this.$dots.includes(document.activeElement))) this.$next.focus({preventScroll: true});
    this.moveThumb(false);
    this.updateVisibility();
  }

  _handleResize(event) {
    if (!this.ready) return;
    super._handleResize(event);
    // KitModule throttles only the leading event; finish a quick breakpoint
    // crossing so hidden dots cannot retain focus or a stale indicator pitch.
    clearTimeout(this.pitchResize);
    this.pitchResize = setTimeout(() => this.onViewportResize(), 250);
  }

  moveThumb(animate = true) {
    // Recover before the ResizeObserver callback if late CSS has exposed dots.
    if (!this.pitch && this.$dots[0].offsetWidth) this.measurePitch();
    let from = {x: this.thumbX || 0, scale: 1};
    if (this.thumbAnimation) {
      const progress = this.thumbAnimation.effect.getComputedTiming().progress ?? 1;
      const segment = progress < 0.5 ? 0 : 1;
      const amount = (progress - segment * 0.5) * 2;
      const start = this.thumbFrames[segment];
      const end = this.thumbFrames[segment + 1];
      from = {x: start.x + (end.x - start.x) * amount, scale: start.scale + (end.scale - start.scale) * amount};
      this.thumbAnimation.cancel();
      this.thumbAnimation = null;
    }
    const to = this.active * this.pitch;
    this.thumbX = to;
    this.$thumb.style.transform = `translateX(${to}px) scaleX(1)`;
    if (!animate || !this.ready || !this.pitch || this.motion.matches || (from.x === to && from.scale === 1)) return;
    // Stretch toward the destination, then let the trailing edge catch up.
    // Sampling the prior keyframes also keeps rapid repeated clicks continuous.
    const left = Math.min(from.x, to);
    const width = Math.max(from.x + this.pitch * from.scale, to + this.pitch) - left;
    this.thumbFrames = [from, {x: left, scale: width / this.pitch}, {x: to, scale: 1}];
    this.thumbAnimation = this.$thumb.animate(this.thumbFrames.map(({x, scale}, index) => ({
      transform: `translateX(${x}px) scaleX(${scale})`, offset: index / 2,
    })), {duration: this.thumbDuration, easing: getComputedStyle(this.$element).getPropertyValue('--loop-indicator-easing').trim() || 'ease-in-out'});
  }

  offset(index) {
    const count = this.$slots.length;
    const distance = (index - this.active + count) % count;
    return distance > count / 2 ? distance - count : distance;
  }

  setCardColor(node, color) {
    if (node.classList.contains(`theme-${color}`)) return;
    cardColors.forEach((name) => node.classList.remove(`theme-${name}`));
    node.classList.add(`theme-${color}`);
  }

  render() {
    const palette = this.$container.classList.contains('theme-blue') ? ['yellow', 'green', 'pink'] :
      cardColors.filter((color) => !this.$container.classList.contains(`theme-${color}`) &&
        !this.$container.classList.contains(`theme-pastel-${color}`));
    this.$slots.forEach((slot, index) => {
      const distance = this.offset(index);
      // Unwrapped positions preserve the color sequence across the loop boundary.
      const color = palette[((this.active + distance) % palette.length + palette.length) % palette.length];
      [this.$headers[index], this.$previews[index]].forEach((node) => {
        this.setCardColor(node, color);
      });
      const previous = Number(slot.dataset.offset);
      // Only recycled neighbors cross the back of the loop without travelling
      // through its center. The incoming/outgoing active card always animates.
      const wrapsBehind = previous * distance < 0 && Math.abs(previous - distance) > this.$slots.length / 2;
      slot.classList.toggle('card-loop__slot--jump', Number.isFinite(previous) && wrapsBehind);
      slot.dataset.offset = distance;
      slot.dataset.visible = Math.abs(distance) <= (this.$slots.length < 5 ? 1 : 2);
      slot.style.setProperty('--loop-offset', distance);
      slot.style.setProperty('--loop-scale', Math.max(0.76, 1 - Math.abs(distance) * 0.12));
      slot.style.zIndex = 10 - Math.abs(distance);
      const article = this.$articles[index];
      article.inert = distance !== 0;
      article.setAttribute('aria-hidden', String(distance !== 0));
      article.setAttribute('role', 'group');
      article.setAttribute('aria-roledescription', 'slide');
      article.setAttribute('aria-label', `${index + 1} of ${this.$slots.length}`);
      const preview = this.$previews[index];
      preview.dataset.offset = distance;
      preview.style.setProperty('--loop-offset', distance);
      preview.style.setProperty('--loop-scale', Math.max(0.76, 1 - Math.abs(distance) * 0.12));
      preview.style.zIndex = 10 - Math.abs(distance);
      preview.hidden = distance === 0 || Math.abs(distance) > (this.$slots.length < 5 ? 1 : 2);
      this.$dots[index].setAttribute('aria-current', String(distance === 0));
      this.$dots[index].setAttribute('tabindex', distance === 0 ? '0' : '-1');
    });
    this.$counter.textContent = `${this.active + 1} / ${this.$slots.length}`;
    this.moveThumb();
  }

  select(index, manual = true) {
    if (!this.ready) return;
    const moveFocus = this.$previews.includes(document.activeElement) || this.$articles[this.active].contains(document.activeElement);
    if (manual) this.stopRotation();
    this.active = (index + this.$slots.length) % this.$slots.length;
    // Move focus before the outgoing article becomes inert or its overlay hides.
    if (moveFocus) (this.pitch ? this.$dots[this.active] : this.$next).focus({preventScroll: true});
    this.render();
    this.remaining = 7000;
    this.progress.currentTime = 0;
    this.$status.textContent = manual ? `${this.active + 1} of ${this.$slots.length}` : '';
    this.schedule();
  }

  onClick(event) {
    if (!this.ready) return;
    if (event.detail > 0 && performance.now() < this.suppressClickUntil) {
      event.preventDefault();
      event.stopPropagation();
      this.suppressClickUntil = 0;
      return;
    }
    const target = event.target.closest('button, a');
    if (!target) return;
    if (target === this.$rotation) {
      this.rotating = !this.rotating && !this.motion.matches;
      this.schedule();
    } else if (target === this.$previous) this.select(this.active - 1);
    else if (target === this.$next) this.select(this.active + 1);
    else if (target.dataset.index === undefined) this.stopRotation();
    else this.select(Number(target.dataset.index));
  }

  stopRotation() {
    this.rotating = false;
    this.schedule();
  }

  schedule() {
    if (this.timer) this.remaining = Math.max(0, this.deadline - performance.now());
    clearTimeout(this.timer);
    this.timer = null;
    this.progress?.pause();
    if (!this.ready) return;
    this.$rotation.setAttribute('aria-label', this.rotating ? 'Pause rotation' : 'Resume rotation');
    this.$rotation.dataset.playing = this.rotating;
    this.$rotation.disabled = this.motion.matches;
    this.$status.setAttribute('aria-live', this.rotating ? 'off' : 'polite');
    if (!this.rotating || !this.visible || this.hovering || this.modalOpen || document.hidden || this.motion.matches) return;
    this.progress.currentTime = 7000 - this.remaining;
    this.progress.play();
    this.deadline = performance.now() + this.remaining;
    this.timer = setTimeout(() => {
      if (!this.ready) return;
      this.timer = null;
      this.select(this.active + 1, false);
    }, this.remaining);
  }

  onPointerDown(event) {
    // Like the site's testimonial carousel, desktop click-and-swipe navigates
    // between slides without freely positioning or reordering cards.
    if (!this.ready || !event.isPrimary || event.button !== 0) return;
    const interactive = event.target.closest('button, a, input, textarea, select, [popover]');
    if (interactive && !this.$previews.includes(interactive)) return;
    if (event.pointerType === 'mouse' && !interactive) event.preventDefault();
    this.suppressClickUntil = 0;
    this.pointer = {id: event.pointerId, x: event.clientX, y: event.clientY, delta: 0, swiping: false};
  }

  onPointerMove(event) {
    if (!this.ready || !this.pointer || this.pointer.id !== event.pointerId) return;
    // A release outside the stage can happen before pointer capture starts.
    if (event.pointerType === 'mouse' && event.buttons === 0) {
      this.pointer = null;
      this.$stage.classList.remove('card-loop__stage--swiping');
      return;
    }
    const dx = event.clientX - this.pointer.x;
    const dy = event.clientY - this.pointer.y;
    if (!this.pointer.swiping) {
      if (Math.abs(dy) > 8 && Math.abs(dy) >= Math.abs(dx)) {
        this.pointer = null;
        return;
      }
      if (Math.abs(dx) <= 8 || Math.abs(dx) <= Math.abs(dy)) return;
      this.pointer.swiping = true;
      this.$stage.setPointerCapture(event.pointerId);
      this.$stage.classList.add('card-loop__stage--swiping');
      this.stopRotation();
    }
    this.pointer.delta = dx;
  }

  onPointerEnd(event) {
    if (!this.ready || !this.pointer || event.pointerId !== this.pointer.id) return;
    // Touch starts with implicit capture on a child. Moving capture to the
    // stage fires a bubbling loss event for that child, not the end of a swipe.
    if (event.type === 'lostpointercapture' && event.target !== this.$stage) return;
    const {delta, swiping, id} = this.pointer;
    this.pointer = null;
    this.$stage.classList.remove('card-loop__stage--swiping');
    if (this.$stage.hasPointerCapture(id)) this.$stage.releasePointerCapture(id);
    if (swiping) {
      this.suppressClickUntil = performance.now() + 350;
      if (event.type === 'pointerup' && Math.abs(delta) > 48) this.select(this.active + (delta < 0 ? 1 : -1));
    }
  }

  unmount() {
    this.ready = false;
    this.$element.setAttribute('data-init-failed', '');
    this.rotating = false;
    clearTimeout(this.timer);
    this.timer = null;
    clearTimeout(this.pitchResize);
    this.dotObserver?.disconnect();
    this.sectionObserver?.disconnect();
    this.progress?.cancel();
    this.thumbAnimation?.cancel();
    this.thumbAnimation = null;
    this.$thumb?.style.removeProperty('transform');
    this.$stage?.classList.remove('card-loop__stage--swiping');
    if (this.pointer && this.$stage?.hasPointerCapture?.(this.pointer.id)) this.$stage.releasePointerCapture(this.pointer.id);
    this.pointer = null;
    (this.listeners || []).forEach((remove) => remove());
    this.listeners = [];
    this.$element.removeAttribute('data-initialized');
    this.$element.removeAttribute('aria-roledescription');
    if (this.$previewsRoot) this.$previewsRoot.hidden = true;
    if (this.$track) this.$track.setAttribute('tabindex', '0');
    [...(this.$headers || []), ...(this.$previews || [])].forEach((node) => this.setCardColor(node, node.dataset.cardColor));
    (this.$articles || []).forEach((article) => {
      article.inert = false;
      ['aria-hidden', 'role', 'aria-roledescription', 'aria-label'].forEach((name) => article.removeAttribute(name));
    });
  }
}

registerModule('CardLoop', CardLoop);
